import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api';
import {
  ApiError,
  type Guild,
  type GuildApplication,
  type GuildListItem,
  type GuildMember,
  type GuildRankWire,
  type GuildRulesWire,
  type GuildVault
} from '../api/types';
import { numero, t } from '../i18n/idioma';
import { nomeDoCargo } from '../i18n/nomes';
import { Abas } from './kit';
import { Brasao } from './hud/icones';
import { Cartao } from './hud/Cartao';

interface Props {
  active: boolean;
  youName: string;
  podeGrupo: boolean;
  onChanged?(text?: string): void;
  onMensagem(nome: string): void;
  onConvidarGrupo(nome: string): void;
}

type Aba = 'membros' | 'cofre' | 'diplomacia' | 'gerenciar' | 'lista' | 'fundar';

const CARGO: Record<GuildRankWire, string> = {
  lider: 'leader',
  comandante: 'commander',
  emissario: 'emissary',
  membro: 'member',
  recruta: 'recruit'
};

const ORDER: GuildRankWire[] = ['lider', 'comandante', 'emissario', 'membro', 'recruta'];

const STATUS_GUERRA: Readonly<Record<string, string>> = {
  proposed: 'guilda.guerraProposta',
  active: 'guilda.guerraAtiva',
  over: 'guilda.guerraEncerrada'
};

const FORMATO_GUERRA: Readonly<Record<string, string>> = {
  kills: 'guilda.guerraMortes',
  time: 'guilda.guerraTempo',
  eternal: 'guilda.guerraEterna'
};

const FORMATO_BOTAO: Readonly<Record<string, string>> = {
  eternal: 'hud.gld.eterna',
  kills: 'hud.gld.porMortes',
  time: 'hud.gld.porTempo'
};

const CLASSE_STATUS: Readonly<Record<string, string>> = {
  proposed: 'p-pill--aviso',
  active: 'p-pill--mal',
  over: ''
};

const MOVIMENTO_COFRE: Readonly<Record<string, string>> = {
  tithe: 'guilda.cofreDizimo',
  withdraw: 'guilda.cofreSaque',
  maintenance: 'guilda.cofreManutencao',
  order: 'guilda.cofreEncomenda',
  war: 'guilda.cofreGuerra'
};

const PERM_ROT: ReadonlyArray<readonly [string, string]> = [
  ['CanInvite', 'hud.perm.CanInvite'],
  ['CanRank', 'hud.perm.CanRank'],
  ['CanMotd', 'hud.perm.CanMotd'],
  ['CanKickRecruit', 'hud.perm.CanKickRecruit'],
  ['CanKickMember', 'hud.perm.CanKickMember'],
  ['CanKickAny', 'hud.perm.CanKickAny'],
  ['CanVault', 'hud.perm.CanVault'],
  ['CanAlliance', 'hud.perm.CanAlliance'],
  ['CanWar', 'hud.perm.CanWar'],
  ['CanVote', 'hud.perm.CanVote'],
  ['CanLead', 'hud.perm.CanLead']
];

const REGRAS_PADRAO = {
  nameMin: 2, nameMax: 20, tagMax: 4, titleMax: 20, warGold: 50_000, allianceCap: 5,
  inviteMinutes: 10, cooldownHours: 72, warKillsMax: 1000, warHoursMax: 168
};

function rotulo(mapa: Readonly<Record<string, string>>, bruto: string): string {
  const chave = mapa[bruto];
  return chave === undefined ? bruto : t(chave);
}

function tempoCurto(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.max(0, ms / 3_600_000);
  if (h < 24) return t('hud.tempo.h', { n: Math.max(1, Math.round(h)) });
  const d = h / 24;
  if (d < 14) return t('hud.tempo.d', { n: Math.round(d) });
  return t('hud.tempo.sem', { n: Math.round(h / 168) });
}

function proximo(r: GuildRankWire, dir: 1 | -1): GuildRankWire | null {
  const i = ORDER.indexOf(r) + dir;
  if (i <= 0 || i >= ORDER.length) return null;
  return ORDER[i];
}

function podeMudar(meu: GuildRankWire, alvo: GuildRankWire, prox: GuildRankWire | null): boolean {
  if (!prox || alvo === 'lider' || prox === 'lider') return false;
  if (meu === 'lider') return true;
  return meu === 'emissario'
    && (alvo === 'recruta' || alvo === 'membro')
    && (prox === 'recruta' || prox === 'membro');
}

function podeExpulsar(me: Guild, alvo: GuildRankWire): boolean {
  if (alvo === 'lider') return false;
  if (me.rank === 'lider') return true;
  if (alvo === 'recruta') return me.canKickRecruit;
  if (alvo === 'membro') return me.canKickMember;
  return false;
}

export function GuildGump({ active, youName, podeGrupo, onChanged, onMensagem, onConvidarGrupo }: Props) {
  const [aba, setAba] = useState<Aba>('membros');
  const [me, setMe] = useState<Guild | null>(null);
  const [list, setList] = useState<readonly GuildListItem[]>([]);
  const [applied, setApplied] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [regrasCarregadas, setRegrasCarregadas] = useState<GuildRulesWire | null>(null);
  const [nome, setNome] = useState('');
  const [tag, setTag] = useState('');
  const [convite, setConvite] = useState('');
  const [soOnline, setSoOnline] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaLista, setBuscaLista] = useState('');
  const [aberto, setAberto] = useState<string | null>(null);
  const [editaTitulo, setEditaTitulo] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [confirma, setConfirma] = useState<string | null>(null);
  const [motd, setMotd] = useState('');
  const [dizimo, setDizimo] = useState(0);
  const [saque, setSaque] = useState('');
  const [allyTag, setAllyTag] = useState('');
  const [warTag, setWarTag] = useState('');
  const [formato, setFormato] = useState<'eternal' | 'kills' | 'time'>('eternal');
  const [guerraN, setGuerraN] = useState(50);
  const [leadName, setLeadName] = useState('');
  const [voteName, setVoteName] = useState('');
  const [apps, setApps] = useState<readonly GuildApplication[]>([]);
  const [vault, setVault] = useState<GuildVault | null>(null);

  const regras = {
    ...REGRAS_PADRAO,
    ...(regrasCarregadas?.const ?? {}),
    perms: regrasCarregadas?.perms ?? {}
  };

  const reload = async () => {
    try {
      const [house, rows] = await Promise.all([api.getGuildMe(), api.listGuilds()]);
      setMe(house);
      setList(rows);
      if (house) {
        setMotd(house.motd);
        setDizimo(house.tithe);
        if (house.canInvite) {
          try { setApps(await api.listGuildApplications()); }
          catch { setApps([]); }
        } else setApps([]);
        try { setVault(await api.getGuildVault()); }
        catch { setVault(null); }
      } else {
        setVault(null);
        setApps([]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('guilda.naoLeu'));
    }
  };

  useEffect(() => {
    void api.getGuildRules().then(setRegrasCarregadas).catch(() => { /* o padrão segura o texto */ });
  }, []);

  useEffect(() => {
    if (!active) return;
    void reload();
    const timer = window.setInterval(() => { void reload(); }, 8000);
    return () => window.clearInterval(timer);
  }, [active]);

  useEffect(() => {
    if (!me && aba !== 'fundar' && aba !== 'lista') setAba('fundar');
    if (me && aba === 'fundar') setAba('membros');
  }, [me, aba]);

  const run = async (fn: () => Promise<void>, ok?: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
      onChanged?.(ok);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  const trocarAba = (id: string) => {
    setAba(id as Aba);
    setAberto(null);
    setConfirma(null);
    setEditaTitulo(false);
  };

  const nomeDe = (tg: string) => list.find(l => l.tag === tg)?.name ?? '';
  const membrosDe = (tg: string) => list.find(l => l.tag === tg)?.members ?? 0;

  const cab = !me ? (
    <div className="p-gld__cab"><div><h3>{t('guilda.semGuilda')}</h3><p>{t('hud.gld.semGuildaDesc')}</p></div></div>
  ) : (
    <Cabeca me={me} onEditar={() => trocarAba('gerenciar')} />
  );

  const abas = !me
    ? [
      { id: 'fundar', rotulo: t('guilda.fundar') },
      { id: 'lista', rotulo: t('hud.gld.guildasDoShard') }
    ]
    : [
      { id: 'membros', rotulo: t('guilda.membros') },
      { id: 'cofre', rotulo: t('guilda.cofre') },
      { id: 'diplomacia', rotulo: t('guilda.diplomacia'), pip: me.canWar ? me.wars.filter(w => w.status === 'proposed').length : undefined },
      { id: 'gerenciar', rotulo: t('guilda.gerenciar'), pip: me.canInvite && apps.length > 0 ? apps.length : undefined },
      { id: 'lista', rotulo: t('hud.gld.guildas') }
    ];

  let painel: ReactNode = null;
  if (me && aba === 'membros') {
    painel = (
      <Membros
        me={me} youName={youName} busca={busca} soOnline={soOnline} convite={convite} busy={busy}
        aberto={aberto} editaTitulo={editaTitulo} titulo={titulo} confirma={confirma}
        regras={regras} podeGrupo={podeGrupo}
        onBusca={setBusca} onSoOnline={setSoOnline} onConvite={setConvite}
        onConvidar={() => void run(async () => { await api.inviteGuild(convite); setConvite(''); }, t('guilda.convidou', { nome: convite }))}
        onAlternar={nomeM => {
          if (nomeM.trim().toLowerCase() === youName.trim().toLowerCase()) return;
          setAberto(atual => atual === nomeM ? null : nomeM);
          setEditaTitulo(false);
          setConfirma(null);
        }}
        onPromover={(m, rank) => void run(() => api.setGuildRank(m, rank))}
        onTitulo={m => { setEditaTitulo(true); setTitulo(m.title); setConfirma(null); }}
        onTituloValor={setTitulo}
        onSalvarTitulo={m => {
          if (!/^[\p{L} ]*$/u.test(titulo)) { setError(t('guilda.titleCharset')); return; }
          void run(async () => { await api.setGuildTitle(m, titulo); setEditaTitulo(false); });
        }}
        onCancelarTitulo={() => setEditaTitulo(false)}
        onMensagem={onMensagem}
        onGrupo={onConvidarGrupo}
        onPedirExpulsar={nomeM => setConfirma(`expulsar:${nomeM}`)}
        onExpulsar={nomeM => void run(async () => { await api.kickGuild(nomeM); setAberto(null); setConfirma(null); })}
        onCancelar={() => setConfirma(null)}
      />
    );
  } else if (me && aba === 'cofre') {
    painel = (
      <Cofre
        me={me} vault={vault} dizimo={dizimo} saque={saque} busy={busy}
        onDizimo={setDizimo} onSaque={setSaque}
        onSalvarDizimo={() => void run(() => api.setGuildTithe(dizimo))}
        onSacar={() => void run(async () => { await api.withdrawGuildVault(Number(saque)); setSaque(''); }, t('guilda.sacou'))}
      />
    );
  } else if (me && aba === 'diplomacia') {
    const falta = regras.warGold - me.gold;
    painel = (
      <Diplomacia
        me={me} regras={regras} busy={busy} allyTag={allyTag} warTag={warTag} formato={formato} guerraN={guerraN}
        confirma={confirma} falta={falta} nomeDe={nomeDe} membrosDe={membrosDe}
        onAlly={setAllyTag} onWarTag={setWarTag} onFormato={setFormato} onN={setGuerraN}
        onProporAlianca={() => void run(async () => { await api.proposeAlliance(allyTag); setAllyTag(''); })}
        onSairAlianca={() => setConfirma('sairAli')}
        onSairAliancaSim={() => void run(async () => { await api.leaveAlliance(); setConfirma(null); })}
        onCancelar={() => setConfirma(null)}
        onAceitar={() => void run(() => api.acceptWar())}
        onRecusar={() => void run(() => api.declineWar())}
        onRender={() => void run(() => api.surrenderWar())}
        onProporGuerra={() => void run(async () => {
          await api.proposeWar(warTag, formato, formato === 'kills' ? guerraN : undefined, formato === 'time' ? guerraN : undefined);
          setWarTag('');
        })}
      />
    );
  } else if (me && aba === 'gerenciar') {
    painel = (
        <Gerenciar
        me={me} youName={youName} apps={apps} regras={regras} busy={busy} motd={motd} voteName={voteName} leadName={leadName} confirma={confirma}
        onMotd={setMotd} onVote={setVoteName} onLead={setLeadName}
        onAceitar={id => void run(() => api.acceptGuildApplication(id))}
        onRecusar={id => void run(() => api.refuseGuildApplication(id))}
        onSalvarMotd={() => void run(() => api.setGuildMotd(motd))}
        onVotar={() => void run(async () => { await api.voteGuild(voteName); })}
        onPedirPassar={() => setConfirma('passar')}
        onPassar={() => void run(() => api.passGuildLead(leadName), t('guilda.passouOCargo'))}
        onPedirSair={() => setConfirma('sair')}
        onSair={() => void run(() => api.leaveGuild(), t('guilda.saiu'))}
        onCancelar={() => setConfirma(null)}
      />
    );
  } else if (aba === 'lista') {
    painel = (
      <Lista
        me={me} list={list} busca={buscaLista} applied={applied} busy={busy}
        onBusca={setBuscaLista}
        onCandidatar={id => void run(async () => {
          await api.applyGuild(id);
          setApplied(cur => new Set(cur).add(id));
        })}
      />
    );
  } else {
    const nomeOk = nome.trim().length >= regras.nameMin && nome.trim().length <= regras.nameMax && /^[\p{L} ]+$/u.test(nome.trim());
    const tagOk = new RegExp(`^\\p{L}{1,${regras.tagMax}}$`, 'u').test(tag.trim());
    painel = (
      <div className="p-gld__fundar">
        <Brasao tag={(tag.trim() || '?').toUpperCase().slice(0, 4)} grande />
        <form className="p-gld__grade" onSubmit={e => {
          e.preventDefault();
          if (!nomeOk || !tagOk) return;
          void run(async () => { await api.createGuild(nome, tag); }, t('guilda.fundou', { tag: tag.toUpperCase(), nome }));
        }}>
          <p className="p-gld__nota">{t('guilda.fundarNaoCusta')} {t('hud.gld.quemFunda')}</p>
          <label className="field">
            <span className="field__label">{t('comum.nome')}</span>
            <input value={nome} maxLength={regras.nameMax} placeholder={t('hud.gld.nomeExemplo')} onChange={e => setNome(e.target.value)} />
            <small className={nome && !nomeOk ? 'p-alerta' : 'muted'}>{t('guilda.nameLength')} {t('hud.gld.soLetras')}</small>
          </label>
          <label className="field">
            <span className="field__label">{t('guilda.tag')}</span>
            <input value={tag} maxLength={regras.tagMax} placeholder={t('hud.gld.tagExemplo')} onChange={e => setTag(e.target.value)} />
            <small className={tag && !tagOk ? 'p-alerta' : 'muted'}>{t('guilda.tagLength')} {t('hud.gld.tagAntes')}</small>
          </label>
          <button type="submit" className="btn btn--primary" disabled={busy || !nomeOk || !tagOk}>{t('guilda.fundar')}</button>
        </form>
      </div>
    );
  }

  return (
    <>
      {cab}
      <Abas className="p-gld__abas" forma="sublinhada" aria-label={t('guilda.titulo')} valor={aba} onMuda={trocarAba} itens={abas} />
      <div className="p-gld__painel">{painel}{error && <p className="notice" role="alert">{error}</p>}</div>
    </>
  );
}

function Cabeca({ me, onEditar }: { me: Guild; onEditar(): void }) {
  const on = me.members.filter(m => m.online).length;
  const ativas = me.wars.filter(w => w.status === 'active').length;
  return (
    <div className="p-gld__cab">
      <Brasao tag={me.tag} />
      <div className="p-gld__id">
        <h3><span className="p-tag is-nossa">[{me.tag}]</span> {me.name}</h3>
        <p className="p-gld__motd">
          {me.motd || t('guilda.semRecado')}
          {me.canMotd && <> <button type="button" className="p-lapis" title={t('hud.gld.editarRecado')} onClick={onEditar}>✎</button></>}
        </p>
        <div className="p-gld__chips">
          <span className="p-chip"><span className="friend-dot is-on" />{t('hud.gld.online', { on, n: me.members.length })}</span>
          <span className="p-chip p-chip--ouro"><span className="p-moeda" />{numero(me.gold)}</span>
          {me.alliance && <span className="p-chip">{t('hud.gld.alianca', { tag: me.alliance.leaderTag, n: me.alliance.tags.length })}</span>}
          {ativas > 0 && <span className="p-chip p-chip--mal">{t('hud.gld.emGuerra', { n: ativas })}</span>}
          <span className="p-chip">{t('hud.gld.voce', { cargo: nomeDoCargo(CARGO[me.rank]) })}</span>
        </div>
      </div>
    </div>
  );
}

function Membros(p: {
  me: Guild; youName: string; busca: string; soOnline: boolean; convite: string; busy: boolean;
  aberto: string | null; editaTitulo: boolean; titulo: string; confirma: string | null;
  regras: { titleMax: number; inviteMinutes: number };
  podeGrupo: boolean;
  onBusca(v: string): void; onSoOnline(v: boolean): void; onConvite(v: string): void; onConvidar(): void;
  onAlternar(nome: string): void;
  onPromover(nome: string, rank: GuildRankWire): void;
  onTitulo(m: GuildMember): void; onTituloValor(v: string): void; onSalvarTitulo(nome: string): void; onCancelarTitulo(): void;
  onMensagem(nome: string): void; onGrupo(nome: string): void;
  onPedirExpulsar(nome: string): void; onExpulsar(nome: string): void; onCancelar(): void;
}) {
  const q = p.busca.trim().toLowerCase();
  const lista = p.me.members.filter(m =>
    (!p.soOnline || m.online)
    && (!q || m.name.toLowerCase().includes(q) || (m.title || '').toLowerCase().includes(q)));
  const grupos = ORDER
    .map(r => [r, lista.filter(m => m.rank === r).sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name))] as const)
    .filter(([, ms]) => ms.length > 0);
  const total = p.me.members.length;
  const on = p.me.members.filter(m => m.online).length;
  return (
    <>
      <div className="p-gld__barra">
        <label className="p-gld__busca">
          <input type="search" placeholder={t('hud.gld.procurarMembro')} aria-label={t('hud.gld.procurarMembro')} value={p.busca} onChange={e => p.onBusca(e.target.value)} />
        </label>
        <div className="p-seg p-seg--mini" role="radiogroup">
          <button type="button" role="radio" aria-checked={!p.soOnline} onClick={() => p.onSoOnline(false)}>{`${t('comum.todos')} · ${total}`}</button>
          <button type="button" role="radio" aria-checked={p.soOnline} onClick={() => p.onSoOnline(true)}>{`${t('comum.online')} · ${on}`}</button>
        </div>
        {p.me.canInvite && (
          <form className="p-gld__convite" onSubmit={e => { e.preventDefault(); p.onConvidar(); }}>
            <input value={p.convite} placeholder={t('hud.gld.convidarPeloNome')} maxLength={16} aria-label={t('guilda.convidar')} onChange={e => p.onConvite(e.target.value)} />
            <button type="submit" className="btn btn--tiny btn--primary" disabled={p.busy}>{t('guilda.convidar')}</button>
          </form>
        )}
      </div>
      <div className="p-gld__lista">
        {grupos.length === 0 ? <p className="muted">{t('hud.gld.ninguem')}</p> : grupos.map(([r, ms]) => (
          <section key={r}>
            <h4>{t(`hud.gld.plural.${r}`)} <span>{ms.length}</span></h4>
            <ul>
              {ms.map(m => {
                const voce = m.name.trim().toLowerCase() === p.youName.trim().toLowerCase();
                const aberto = p.aberto === m.name;
                const sobe = proximo(m.rank, -1);
                const desce = proximo(m.rank, 1);
                return (
                  <li key={m.name} className={`p-gld__m${aberto ? ' is-aberto' : ''}${voce ? ' is-voce' : ''}`}>
                    <button type="button" className="p-gld__linha" aria-expanded={aberto} onClick={() => p.onAlternar(m.name)}>
                      <span className={m.online ? 'friend-dot is-on' : 'friend-dot'} />
                      <span className="p-gld__nome">
                        <b>{m.name}</b>
                        {m.vip && <> <span className="p-coroa" title="VIP">♛</span></>}
                        {voce && <> <em className="p-voce">{t('hud.gld.voceCurto')}</em></>}
                        {m.title && <small>“{m.title}”</small>}
                      </span>
                      <span className="p-gld__dado" title={t('hud.gld.dizimo')}>{m.tithe}%</span>
                      <span className="p-gld__dado p-gld__dado--tempo" title={t('hud.gld.naGuilda')}>{tempoCurto(m.joinedAt)}</span>
                      <span className="p-gld__seta">{voce ? '' : aberto ? '▴' : '▾'}</span>
                    </button>
                    {aberto && !voce && (
                      <div className="p-gld__acoes">
                        {p.editaTitulo ? (
                          <form className="p-gld__titulo" onSubmit={e => { e.preventDefault(); p.onSalvarTitulo(m.name); }}>
                            <input value={p.titulo} maxLength={p.regras.titleMax} placeholder={t('hud.gld.tituloPlaceholder')} aria-label={t('hud.gld.tituloDe', { nome: m.name })} onChange={e => p.onTituloValor(e.target.value)} />
                            <button type="submit" className="btn btn--tiny btn--primary">{t('hud.salvar')}</button>
                            <button type="button" className="btn btn--tiny btn--ghost" onClick={p.onCancelarTitulo}>{t('comum.cancelar')}</button>
                          </form>
                        ) : (
                          <>
                            {podeMudar(p.me.rank, m.rank, sobe) && sobe && (
                              <button type="button" className="btn btn--tiny" onClick={() => p.onPromover(m.name, sobe)}>▲ {t('guilda.promover')} a {nomeDoCargo(CARGO[sobe])}</button>
                            )}
                            {podeMudar(p.me.rank, m.rank, desce) && desce && (
                              <button type="button" className="btn btn--tiny" onClick={() => p.onPromover(m.name, desce)}>▼ {t('guilda.rebaixar')} a {nomeDoCargo(CARGO[desce])}</button>
                            )}
                            {p.me.canMotd && <button type="button" className="btn btn--tiny" onClick={() => p.onTitulo(m)}>{t('guilda.titulo2')}</button>}
                            <button type="button" className="btn btn--tiny btn--ghost" onClick={() => p.onMensagem(m.name)}>{t('hud.gld.mensagem')}</button>
                            {m.online && (
                              <button type="button" className="btn btn--tiny btn--ghost" disabled={!p.podeGrupo} onClick={() => p.onGrupo(m.name)}>{t('hud.convidarGrupo')}</button>
                            )}
                            {podeExpulsar(p.me, m.rank) && (p.confirma === `expulsar:${m.name}` ? (
                              <span className="p-confirma">{t('guilda.expulsarConfirma', { nome: m.name })}
                                <button type="button" className="btn btn--tiny btn--danger" onClick={() => p.onExpulsar(m.name)}>{t('guilda.expulsar')}</button>
                                <button type="button" className="btn btn--tiny btn--ghost" onClick={p.onCancelar}>{t('comum.cancelar')}</button>
                              </span>
                            ) : (
                              <button type="button" className="btn btn--tiny btn--danger" onClick={() => p.onPedirExpulsar(m.name)}>{t('guilda.expulsar')}</button>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      <p className="p-gld__dica">{t('hud.gld.dicaMembros')}{p.me.canInvite && ` ${t('hud.gld.conviteVale', { n: p.regras.inviteMinutes })}`}</p>
    </>
  );
}

function Cofre(p: {
  me: Guild; vault: GuildVault | null; dizimo: number; saque: string; busy: boolean;
  onDizimo(n: number): void; onSaque(v: string): void; onSalvarDizimo(): void; onSacar(): void;
}) {
  const entries = p.vault?.entries ?? [];
  return (
    <div className="p-gld__grade">
      <Cartao titulo={t('hud.gld.cofreTitulo')} desc={t('hud.gld.cofreDesc')}>
        <p className="p-gld__ouro"><span className="p-moeda" />{numero(p.me.gold)}<small>{t('hud.gld.deOuro')}</small></p>
      </Cartao>
      <Cartao titulo={t('guilda.seuDizimo')} desc={t('guilda.dizimoDepois')}>
        <div className="p-gld__dizimo">
          <input type="range" min={0} max={10} value={p.dizimo} aria-label={t('guilda.seuDizimo')} onChange={e => p.onDizimo(Number(e.target.value))} />
          <b>{p.dizimo}%</b>
          <button type="button" className="btn btn--tiny" onClick={p.onSalvarDizimo}>{t('hud.salvar')}</button>
        </div>
        <small className="p-gld__nota">{t('hud.gld.dizimoNota')}</small>
      </Cartao>
      {p.me.canVault && (
        <Cartao titulo={t('guilda.sacar')} desc={t('hud.gld.sacarDesc')}>
          <form className="p-gld__saque" onSubmit={e => { e.preventDefault(); p.onSacar(); }}>
            <input type="number" min={1} max={p.me.gold} value={p.saque} placeholder={t('hud.gld.quanto')} aria-label={t('hud.gld.quanto')} onChange={e => p.onSaque(e.target.value)} />
            <button type="button" className="btn btn--tiny btn--ghost" onClick={() => p.onSaque(String(p.me.gold))}>{t('hud.gld.tudo')}</button>
            <button type="submit" className="btn btn--tiny btn--primary" disabled={p.busy}>{t('guilda.sacar')}</button>
          </form>
        </Cartao>
      )}
      <Cartao titulo={t('hud.gld.movimentos')} desc="">
        {entries.length === 0 ? <p className="muted">{t('hud.gld.semMovimento')}</p> : (
          <table className="p-gld__tab">
            <thead><tr><th>{t('hud.gld.quando')}</th><th>{t('hud.gld.quem')}</th><th>{t('hud.gld.tipo')}</th><th>{t('hud.gld.valor')}</th></tr></thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={`${e.at}-${i}`}>
                  <td>{t('hud.tempo.ha', { t: tempoCurto(e.at) })}</td>
                  <td>{e.name}</td>
                  <td><span className="p-pill">{rotulo(MOVIMENTO_COFRE, e.kind)}</span></td>
                  <td className={e.amount >= 0 ? 'p-mais' : 'p-menos'}>{e.amount >= 0 ? '+' : '−'}{numero(Math.abs(e.amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Cartao>
      <Cartao titulo={<>{t('guilda.encomenda')} <em className="p-nova">{t('comum.emBreve')}</em></>} desc={t('guilda.encomendaDepois')}>
        <button type="button" className="btn btn--tiny" disabled>{t('guilda.abrirEncomenda')}</button>
      </Cartao>
    </div>
  );
}

function Diplomacia(p: {
  me: Guild;
  regras: { tagMax: number; allianceCap: number; warGold: number; inviteMinutes: number; warKillsMax: number; warHoursMax: number };
  busy: boolean; allyTag: string; warTag: string; formato: 'eternal' | 'kills' | 'time'; guerraN: number;
  confirma: string | null; falta: number;
  nomeDe(tag: string): string; membrosDe(tag: string): number;
  onAlly(v: string): void; onWarTag(v: string): void; onFormato(f: 'eternal' | 'kills' | 'time'): void; onN(n: number): void;
  onProporAlianca(): void; onSairAlianca(): void; onSairAliancaSim(): void; onCancelar(): void;
  onAceitar(): void; onRecusar(): void; onRender(): void; onProporGuerra(): void;
}) {
  const a = p.me.alliance;
  const ali = a ? (
    <>
      <div className="p-gld__ali">{a.tags.map(tag => (
        <div className="p-gld__casa" key={tag}>
          {tag === a.leaderTag && <span className="p-coroa" title={t('hud.gld.cabeca')}>♛</span>}
          <span className={`p-tag${tag === p.me.tag ? ' is-nossa' : ''}`} title={p.nomeDe(tag)}>[{tag}]</span>
          <span>{p.nomeDe(tag)}</span><small>{t('hud.gld.membrosN', { n: numero(p.membrosDe(tag)) })}</small>
        </div>
      ))}</div>
      <p className="p-gld__nota">{t('hud.gld.casas', { n: a.tags.length, max: p.regras.allianceCap, tag: a.leaderTag })}</p>
      {p.me.canAlliance && (p.confirma === 'sairAli' ? (
        <span className="p-confirma">{t('hud.gld.sairAliancaConfirma')}
          <button type="button" className="btn btn--tiny btn--danger" onClick={p.onSairAliancaSim}>{t('guilda.sairDaAlianca')}</button>
          <button type="button" className="btn btn--tiny btn--ghost" onClick={p.onCancelar}>{t('comum.cancelar')}</button>
        </span>
      ) : (
        <button type="button" className="btn btn--tiny" onClick={p.onSairAlianca}>{t('guilda.sairDaAlianca')}</button>
      ))}
    </>
  ) : p.me.canAlliance ? (
    <form className="p-gld__linhaf" onSubmit={e => { e.preventDefault(); p.onProporAlianca(); }}>
      <input maxLength={p.regras.tagMax} placeholder={t('guilda.tag')} aria-label={t('guilda.tag')} value={p.allyTag} onChange={e => p.onAlly(e.target.value)} />
      <button type="submit" className="btn btn--tiny btn--primary">{t('guilda.propor')}</button>
    </form>
  ) : <p className="muted">{t('guilda.soLiderPropoe')}</p>;

  const guerras = p.me.wars.length === 0 ? <p className="muted">{t('hud.gld.semGuerra')}</p> : p.me.wars.map(w => (
    <div className="p-gld__guerra" key={w.tag}>
      <span className="p-tag" title={p.nomeDe(w.tag)}>[{w.tag}]</span>
      <span><b>{p.nomeDe(w.tag) || w.tag}</b><small>{rotulo(FORMATO_GUERRA, w.format)}</small></span>
      <span className={`p-pill ${CLASSE_STATUS[w.status] ?? ''}`}>{rotulo(STATUS_GUERRA, w.status)}</span>
      <span className="p-gld__acoesg">
        {p.me.canWar && w.status === 'proposed' && (
          <>
            <button type="button" className="btn btn--tiny btn--primary" onClick={p.onAceitar}>{t('comum.aceitar')}</button>
            <button type="button" className="btn btn--tiny" onClick={p.onRecusar}>{t('comum.recusar')}</button>
          </>
        )}
        {p.me.canWar && w.status === 'active' && (
          <button type="button" className="btn btn--tiny btn--danger" onClick={p.onRender}>{t('guilda.renderSe')}</button>
        )}
      </span>
    </div>
  ));

  return (
    <div className="p-gld__grade">
      <Cartao titulo={t('guilda.alianca')} desc={t('hud.gld.aliancaDesc')}>{ali}</Cartao>
      <Cartao titulo={t('guilda.guerra')} desc={t('guilda.guerraDepois')}>
        {guerras}
        {p.me.canWar && (
          <form className="p-gld__propor" onSubmit={e => { e.preventDefault(); p.onProporGuerra(); }}>
            <h4>{t('guilda.proporGuerra')}</h4>
            <div className="p-gld__linhaf">
              <input maxLength={p.regras.tagMax} placeholder={t('guilda.tagRival')} aria-label={t('guilda.tagRival')} value={p.warTag} onChange={e => p.onWarTag(e.target.value)} />
              <div className="p-seg p-seg--mini" role="radiogroup">
                {(['eternal', 'kills', 'time'] as const).map(f => (
                  <button type="button" role="radio" aria-checked={p.formato === f} key={f} onClick={() => p.onFormato(f)}>{t(FORMATO_BOTAO[f])}</button>
                ))}
              </div>
              {p.formato !== 'eternal' && (
                <label className="p-gld__n">
                  <input
                    type="number"
                    min={1}
                    max={p.formato === 'kills' ? p.regras.warKillsMax : p.regras.warHoursMax}
                    value={p.guerraN}
                    aria-label={t(p.formato === 'kills' ? 'hud.gld.mortesParaVencer' : 'hud.gld.horas')}
                    onChange={e => p.onN(Number(e.target.value))}
                  /> {t(p.formato === 'kills' ? 'hud.gld.mortes' : 'hud.gld.horasCurto')}
                </label>
              )}
              <button type="submit" className="btn btn--tiny" disabled={p.busy || p.falta > 0}>{t('guilda.propor')}</button>
            </div>
            <p className="p-gld__nota">
              {t('hud.gld.custoGuerraA')} <b>{t('hud.gld.custoGuerraOuro', { ouro: numero(p.regras.warGold) })}</b> {t('hud.gld.custoGuerraB', { min: p.regras.inviteMinutes })}
              {p.falta > 0 && <> <span className="p-alerta">{t('hud.gld.falta', { tem: numero(p.me.gold), falta: numero(p.falta) })}</span></>}
            </p>
          </form>
        )}
      </Cartao>
    </div>
  );
}

function Gerenciar(p: {
  me: Guild; apps: readonly GuildApplication[]; youName: string;
  regras: { cooldownHours: number; perms: Readonly<Record<string, readonly string[]>> };
  busy: boolean; motd: string; voteName: string; leadName: string; confirma: string | null;
  onMotd(v: string): void; onVote(v: string): void; onLead(v: string): void;
  onAceitar(id: string): void; onRecusar(id: string): void; onSalvarMotd(): void; onVotar(): void;
  onPedirPassar(): void; onPassar(): void; onPedirSair(): void; onSair(): void; onCancelar(): void;
}) {
  const elegiveis = p.me.members.filter(m => m.rank !== 'recruta');
  const precisa = Math.floor(elegiveis.length / 2) + 1;

  return (
    <div className="p-gld__grade">
      {p.me.canInvite && (
        <Cartao titulo={<>{t('hud.gld.candidaturas')} <span className="kit-pip">{p.apps.length}</span></>}>
          {p.apps.length === 0 ? <p className="muted">{t('hud.gld.semCandidatura')}</p> : p.apps.map(a => (
            <div className="p-gld__cand" key={a.id}>
              <b>{a.name}</b><small>{t('hud.tempo.ha', { t: tempoCurto(a.createdAt) })}</small>
              <button type="button" className="btn btn--tiny btn--primary" onClick={() => p.onAceitar(a.id)}>{t('comum.aceitar')}</button>
              <button type="button" className="btn btn--tiny" onClick={() => p.onRecusar(a.id)}>{t('comum.recusar')}</button>
            </div>
          ))}
        </Cartao>
      )}
      {p.me.canMotd && (
        <Cartao titulo={t('guilda.recado')} desc={t('hud.gld.recadoDesc')}>
          <form className="p-gld__recado" onSubmit={e => { e.preventDefault(); p.onSalvarMotd(); }}>
            <textarea maxLength={80} rows={2} aria-label={t('guilda.recado')} value={p.motd} onChange={e => p.onMotd(e.target.value)} />
            <div className="p-gld__linhaf"><small>{p.motd.length}/80</small>
              <button type="submit" className="btn btn--tiny btn--primary">{t('guilda.salvarRecado')}</button></div>
          </form>
        </Cartao>
      )}
      {p.me.canVote && (
        <Cartao titulo={t('hud.gld.eleicao')} desc={t('hud.gld.eleicaoDesc', { precisa, elegiveis: elegiveis.length })}>
          {p.me.votes.length === 0 ? <p className="muted">{t('hud.gld.ninguemVotou')}</p> : p.me.votes.map(v => (
            <div className="p-gld__voto" key={v.name}>
              <span>{v.name}</span>
              <span className="p-gld__barrav"><i style={{ width: `${Math.min(100, v.count / precisa * 100)}%` }} /></span>
              <small>{t('hud.gld.votosDe', { n: v.count, de: precisa })}</small>
            </div>
          ))}
          <form className="p-gld__linhaf" onSubmit={e => { e.preventDefault(); p.onVotar(); }}>
            <select aria-label={t('guilda.votarEm')} value={p.voteName} onChange={e => p.onVote(e.target.value)}>
              <option value="" />
              {elegiveis.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
            <button type="submit" className="btn btn--tiny">{t('guilda.votar')}</button>
          </form>
        </Cartao>
      )}
      {p.me.canLead && (
        <Cartao titulo={t('guilda.passarLideranca')} desc={t('hud.gld.passarDesc')}>
          {p.confirma === 'passar' ? (
            <span className="p-confirma">{t('hud.gld.passarConfirma', { nome: p.leadName })}
              <button type="button" className="btn btn--tiny btn--danger" onClick={p.onPassar}>{t('guilda.passar')}</button>
              <button type="button" className="btn btn--tiny btn--ghost" onClick={p.onCancelar}>{t('comum.cancelar')}</button>
            </span>
          ) : (
            <form className="p-gld__linhaf" onSubmit={e => { e.preventDefault(); if (p.leadName) p.onPedirPassar(); }}>
              <select aria-label={t('hud.gld.novoLider')} value={p.leadName} onChange={e => p.onLead(e.target.value)}>
                <option value="" />
                {p.me.members.filter(m => m.name.trim().toLowerCase() !== p.youName.trim().toLowerCase()).map(m => (
                  <option key={m.name} value={m.name}>{m.name}</option>
                ))}
              </select>
              <button type="submit" className="btn btn--tiny">{t('guilda.passar')}</button>
            </form>
          )}
        </Cartao>
      )}
      <Cartao titulo={t('hud.gld.cargos')} desc={t('hud.gld.cargosDesc')}>
        <div className="p-gld__rolar"><table className="p-gld__tab p-gld__perm">
          <thead><tr><th />{ORDER.map(r => <th className={r === p.me.rank ? 'is-meu' : undefined} key={r}>{nomeDoCargo(CARGO[r])}</th>)}</tr></thead>
          <tbody>{PERM_ROT.map(([perm, chave]) => (
            <tr key={perm}><td>{t(chave)}</td>
              {ORDER.map(r => <td className={r === p.me.rank ? 'is-meu' : undefined} key={r}>{p.regras.perms[perm]?.includes(r) ? '✓' : '·'}</td>)}
            </tr>
          ))}</tbody>
        </table></div>
      </Cartao>
      <Cartao titulo={t('guilda.sairDaGuilda')} desc={t('hud.gld.sairDesc', { h: p.regras.cooldownHours })}>
        {p.me.rank === 'lider' ? <p className="p-alerta">{t('guilda.passLead')}</p> : p.confirma === 'sair' ? (
          <span className="p-confirma">{t('hud.gld.sairConfirma', { tag: p.me.tag })}
            <button type="button" className="btn btn--tiny btn--danger" onClick={p.onSair}>{t('guilda.sairDaGuilda')}</button>
            <button type="button" className="btn btn--tiny btn--ghost" onClick={p.onCancelar}>{t('comum.cancelar')}</button>
          </span>
        ) : (
          <button type="button" className="btn btn--tiny btn--danger" onClick={p.onPedirSair}>{t('guilda.sairDaGuilda')}</button>
        )}
      </Cartao>
    </div>
  );
}

function Lista(p: {
  me: Guild | null; list: readonly GuildListItem[]; busca: string; applied: ReadonlySet<string>; busy: boolean;
  onBusca(v: string): void; onCandidatar(id: string): void;
}) {
  const q = p.busca.trim().toLowerCase();
  const linhas = p.list.filter(l => !q || l.name.toLowerCase().includes(q) || l.tag.toLowerCase().includes(q));
  const guerra = new Set((p.me?.wars ?? []).filter(w => w.status === 'active').map(w => w.tag));
  const aliadas = new Set(p.me?.alliance?.tags ?? []);
  return (
    <>
      <div className="p-gld__barra">
        <label className="p-gld__busca">
          <input type="search" placeholder={t('hud.gld.procurarGuilda')} aria-label={t('hud.gld.procurarGuilda')} value={p.busca} onChange={e => p.onBusca(e.target.value)} />
        </label>
        <small className="muted">{t('hud.gld.noShard', { n: p.list.length })}</small>
      </div>
      <div className="p-gld__rolar"><table className="p-gld__tab p-gld__guildas">
        <thead><tr>
          <th>{t('guilda.tag')}</th><th>{t('comum.nome')}</th><th>{t('guilda.membros')}</th>
          <th>{t('hud.gld.lider')}</th><th>{t('guilda.alianca')}</th><th />
        </tr></thead>
        <tbody>
          {linhas.length === 0 ? <tr><td colSpan={6}>{t('guilda.nenhumaNoShard')}</td></tr> : linhas.map(l => {
            const minha = l.tag === p.me?.tag;
            const marca = minha
              ? <span className="p-pill">{t('hud.gld.sua')}</span>
              : guerra.has(l.tag) && p.me
                ? <span className="p-pill p-pill--mal">{t('hud.gld.emGuerraCurto')}</span>
                : aliadas.has(l.tag) && p.me
                  ? <span className="p-pill p-pill--bem">{t('hud.gld.aliada')}</span>
                  : null;
            return (
              <tr key={l.id}>
                <td><span className={`p-tag${minha ? ' is-nossa' : ''}`}>[{l.tag}]</span></td>
                <td><b>{l.name}</b> {marca}</td>
                <td>{numero(l.members)}</td>
                <td>{l.leader}</td>
                <td>{l.allianceTag ? `[${l.allianceTag}]` : '—'}</td>
                <td>
                  {!p.me && (p.applied.has(l.id)
                    ? <span className="p-pill">{t('guilda.candidaturaEnviada')}</span>
                    : <button type="button" className="btn btn--tiny" disabled={p.busy} onClick={() => p.onCandidatar(l.id)}>{t('guilda.candidatarSe')}</button>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
    </>
  );
}
